import React, { useState } from 'react'
import { Switch } from '@headlessui/react'
import classNames from '../../utils/classNames'
import { ExclamationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid'

interface ComponentProps {
  checked: boolean
  onChange: (value: boolean) => void
  label: string | JSX.Element
  labelClassName?: string
  isRendering?: boolean
  isDisabled?: boolean
  dataTestId?: string
}

const ToggleLine = ({
  checked,
  onChange,
  label,
  labelClassName,
  isRendering,
  isDisabled,
  dataTestId,
}: ComponentProps) => {
  if (isRendering) {
    return (
      <div className='w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
        <div className="flex items-center text-gray-700">
          <div className="h-5 w-5 mr-2 bg-gray-200 rounded-full animate-pulsing"></div>
          <div className="flex flex-col">
            <div className="h-4 w-32 bg-gray-200 rounded-md animate-pulsing mb-1"></div>
            <div className="h-2 w-48 bg-gray-200 rounded-md animate-pulsing"></div>
          </div>
        </div>
        <div className="h-6 w-11 bg-gray-200 rounded-full flex-shrink-0 relative">
          <div className="h-6 w-6 bg-gray-100 rounded-full absolute translate-x-0 animate-pulsing"></div>
        </div>
      </div>
    )
  } else {
    return (
      <Switch.Group
        as='div'
        className={classNames(
          'w-full flex items-center justify-between p-2 cursor-pointer bg-gray-50 hover:bg-gray-100 rounded-lg',
          isDisabled ? "pointer-events-none" : ""
        )}
        onClick={() => onChange(!checked)}
      >
        <Switch.Label as='span' className={classNames("mr-3", labelClassName)}>
          {label}
        </Switch.Label>
        <Switch
          checked={checked}
          onChange={onChange}
          className={classNames(
            checked ? 'bg-green-600' : 'bg-gray-200',
            isDisabled ? "opacity-30" : "",
            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2'
          )}
          disabled={isDisabled}
          data-testid={dataTestId}
        >
          <span
            aria-hidden='true'
            className={classNames(
              checked ? 'translate-x-5' : 'translate-x-0',
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
            )}
          />
        </Switch>
      </Switch.Group>
    )
  }
}

export default ToggleLine
